import { Box, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import PosterTile from "../../components/PosterTile.js";

/**
 * Placeholder shown while a collection dialog's items are loading.
 *
 * Renders a header block standing in for the title/description, followed by a
 * grid of 6 skeleton poster tiles whose size, aspect ratio and column count
 * mirror the real grid in CollectionWithVirtualisation, so the skeleton lands
 * where the actual content will and avoids a layout shift. Each tile reuses
 * PosterTile's `skeleton` prop to stay pixel-identical to the real tiles without
 * triggering any data fetch.
 *
 * @param {boolean} fullScreen - Matches the dialog's fullScreen flag; drives the
 *                               column count exactly as the real grid does.
 * @param {number}  padding    - Outer padding (MUI spacing units). Defaults to 2
 *                               to match the dialog content area; pass 0 when the
 *                               parent already provides padding.
 * @param {boolean} showHeader - Whether to render the title/description placeholder.
 *                               Disable it when the real metadata is already shown
 *                               above (e.g. the grid's measurement gap).
 */
export default function CollectionSkeleton({
  fullScreen = false,
  padding = 2,
  showHeader = true,
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Mirror NUM_COLUMNS from CollectionWithVirtualisation so tiles line up.
  const cols = fullScreen ? 3 : isMdUp ? 6 : 4;

  return (
    <Box
      sx={{
        p: padding,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2, // matches the dialog's gap between metadata and grid
      }}
    >
      {showHeader && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Title */}
          <Skeleton variant="text" width="55%" sx={{ fontSize: "1.5rem" }} />
          {/* Description */}
          <Skeleton variant="text" width="100%" />
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: "12px", // matches the grid's 12px gutter (MUI spacing of 2)
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <PosterTile key={i} skeleton />
        ))}
      </Box>
    </Box>
  );
}
